import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Save,
  ArrowLeft,
  User,
  Mail,
  Shield,
  Building2,
  Eye,
  EyeOff,
  Store,
} from 'lucide-react';
import { getApiUrl } from '../utils/api';
import { HelpIcon } from '../components/HelpIcon';

interface Company {
  id: string;
  name: string;
}

interface Shop {
  id: string;
  name: string;
  email?: string | null;
  companyId?: string | null;
}

const AdminCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [shops, setShops] = useState<Shop[]>([]);
  const [assignedShopIds, setAssignedShopIds] = useState<string[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: '',
    companyId: '',
    isActive: true,
    screenSaverLock: false,
  });

  const roles = [
    { value: 'SUPER_ADMIN', label: 'Super Admin' },
    { value: 'ADMIN', label: 'Admin' },
    { value: 'COMPANY_ADMIN', label: 'Admin Entreprise' },
    { value: 'SHOP_ADMIN', label: 'Admin Shop' },
    { value: 'MANAGER', label: 'Manager (une boutique, sans suppression)' },
  ];

  useEffect(() => {
    fetchCompanies();
    fetchShops();
  }, []);

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/api/admin/companies'), {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCompanies(data.data.companies);
      }
    } catch (err) {
      console.error('Erreur chargement entreprises:', err);
    }
  };

  const fetchShops = async () => {
    try {
      const token = localStorage.getItem('token');
      // allShops=true: this page needs the full shop catalog, not just the admin's current shop
      const response = await fetch(getApiUrl('/api/admin/shops?limit=1000&allShops=true'), {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setShops(data.data?.shops || []);
      }
    } catch (err) {
      console.error('Erreur chargement boutiques:', err);
    }
  };

  const toggleShop = (shopId: string) => {
    const shop = shops.find((item) => item.id === shopId);
    setAssignedShopIds((prev) => {
      const isRemoving = prev.includes(shopId);
      const next = isRemoving
        ? prev.filter((item) => item !== shopId)
        : [...prev, shopId];

      if (!isRemoving && shop?.companyId && formData.role !== 'SUPER_ADMIN') {
        setFormData((current) => ({
          ...current,
          companyId: shop.companyId || current.companyId,
        }));
      }

      return next;
    });
  };

  const selectSingleShop = (shopId: string) => {
    if (!shopId) {
      setAssignedShopIds([]);
      setFormData((current) => ({
        ...current,
        email: current.role === 'MANAGER' ? '' : current.email,
      }));
      return;
    }

    const shop = shops.find((item) => item.id === shopId);
    setAssignedShopIds([shopId]);
    if (shop) {
      setFormData((current) => ({
        ...current,
        companyId: shop.companyId || current.companyId,
        ...(current.role === 'MANAGER' ? { email: shop.email || '' } : {}),
      }));
    }
  };

  const visibleShops =
    formData.role === 'SUPER_ADMIN'
      ? []
      : formData.companyId
        ? shops.filter((shop) => shop.companyId === formData.companyId)
        : shops;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.password || !formData.role || (formData.role !== 'MANAGER' && !formData.email)) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (formData.password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    const requiresCompany =
      formData.role === 'SHOP_ADMIN' ||
      formData.role === 'COMPANY_ADMIN';

    if (requiresCompany && !formData.companyId && assignedShopIds.length === 0) {
      setError('Sélectionnez une entreprise ou assignez au moins une boutique');
      return;
    }

    if (formData.role === 'SUPER_ADMIN' && assignedShopIds.length > 0) {
      setError('Un Super Admin ne peut pas être assigné à des boutiques');
      return;
    }

    if (formData.role === 'MANAGER' && assignedShopIds.length !== 1) {
      setError('Un Manager doit être assigné à une et une seule boutique');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setErrorDetails([]);
      const token = localStorage.getItem('token');

      const response = await fetch(getApiUrl('/api/admin/admins'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          role: formData.role,
          isActive: formData.isActive,
          companyId: formData.companyId || null,
          permissions: { screenSaverLock: formData.screenSaverLock },
          shopIds: assignedShopIds,
        }),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        const reason = data?.message || "Erreur lors de la création de l'admin";
        setError(reason);
        setErrorDetails(Array.isArray(data?.errors) ? data.errors : []);
        return;
      }

      navigate('/admins');
    } catch (err) {
      console.error('Erreur création admin:', err);
      setError('Erreur de connexion au serveur');
      setErrorDetails([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData((prev) => {
      const next = {
        ...prev,
        [field]: value,
      };

      if (field === 'role' && value === 'SUPER_ADMIN') {
        setAssignedShopIds([]);
        next.companyId = '';
        next.screenSaverLock = false;
      }
      if (field === 'role' && value === 'MANAGER') {
        setAssignedShopIds((current) => current.slice(0, 1));
        const selectedShop = shops.find((shop) => shop.id === assignedShopIds[0]);
        next.email = selectedShop?.email || '';
      }

      if (field === 'companyId' && value && prev.role !== 'SUPER_ADMIN') {
        setAssignedShopIds((current) =>
          current.filter((shopId) => {
            const shop = shops.find((item) => item.id === shopId);
            return shop?.companyId === value;
          }),
        );
      }

      return next;
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: '',
      companyId: '',
      isActive: true,
      screenSaverLock: false,
    });
    setAssignedShopIds([]);
    setError(null);
    setErrorDetails([]);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admins')}
          className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 inline-flex items-center space-x-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
            <span>Créer un Admin</span>
            <HelpIcon
              docPath="09-gestion-administrateurs#09-3-creation"
              tooltip="Documentation : Création d'un administrateur"
            />
          </h1>
          <p className="text-gray-600">Ajouter un nouvel administrateur au système</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 font-medium">{error}</p>
          {errorDetails.length > 0 && (
            <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
              {errorDetails.map((detail, i) => (
                <li key={i}>{detail}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="bg-white shadow rounded-lg p-6">
        <form key="admin-create-form" onSubmit={handleSubmit} autoComplete="off" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Nom complet *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  key="admin-create-name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="input-field pl-10"
                  placeholder="Nom complet"
                  autoComplete="off"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Email *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  key="admin-create-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="input-field pl-10"
                  placeholder={formData.role === 'MANAGER' ? 'Email de la boutique sélectionnée' : 'email@example.com'}
                  autoComplete="off"
                  required={formData.role !== 'MANAGER'}
                  disabled={formData.role === 'MANAGER'}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Mot de passe *
              </label>
              <div className="relative">
                <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  key="admin-create-password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  className="input-field pl-10 pr-10"
                  placeholder="Mot de passe"
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500">Minimum 8 caractères</p>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Rôle *
              </label>
              <select
                value={formData.role}
                onChange={(e) => handleInputChange('role', e.target.value)}
                className="input-field"
                required
              >
                <option value="">Sélectionner un rôle</option>
                {roles.map((role) => (
                  <option key={role.value} value={role.value}>{role.label}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Entreprise
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <select
                  value={formData.companyId}
                  onChange={(e) => handleInputChange('companyId', e.target.value)}
                  className="input-field pl-10"
                  disabled={formData.role === 'SUPER_ADMIN'}
                >
                  <option value="">Aucune entreprise</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-gray-500">
                {formData.role === 'SUPER_ADMIN'
                  ? 'Laisser vide pour les Super Admins'
                  : formData.role === 'MANAGER'
                    ? 'Sélectionnez exactement une boutique ci-dessous : l’entreprise est alignée automatiquement.'
                    : 'Obligatoire pour Admin Entreprise / Admin Shop / Manager, ou sélectionnez une boutique ci-dessous.'}
              </p>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Statut
              </label>
              <select
                value={formData.isActive ? 'true' : 'false'}
                onChange={(e) => handleInputChange('isActive', e.target.value === 'true')}
                className="input-field"
              >
                <option value="true">Actif</option>
                <option value="false">Inactif</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Permissions
              </label>
              <label className="flex min-h-[42px] items-center gap-3 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={formData.screenSaverLock}
                  onChange={(e) => handleInputChange('screenSaverLock', e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  disabled={formData.role === 'SUPER_ADMIN'}
                />
                <span>Screen saver</span>
              </label>
            </div>
          </div>

          {formData.role && formData.role !== 'SUPER_ADMIN' && shops.length > 0 && (
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Store className="h-5 w-5 text-gray-500" />
                  Boutiques assignées
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {formData.role === 'MANAGER'
                    ? "Sélectionnez exactement une boutique. Son email sera utilisé comme identifiant de connexion."
                    : "Cochez les boutiques gérées par cet admin. L’entreprise sera automatiquement alignée sur celle de la boutique sélectionnée."}
                </p>
              </div>

              {formData.role === 'MANAGER' ? (
                <select
                  value={assignedShopIds[0] || ''}
                  onChange={(e) => selectSingleShop(e.target.value)}
                  className="input-field max-w-md"
                  required
                >
                  <option value="">Sélectionner une boutique</option>
                  {visibleShops.map((shop) => (
                    <option key={shop.id} value={shop.id}>{shop.name}</option>
                  ))}
                </select>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {visibleShops.map((shop) => (
                      <label
                        key={shop.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          assignedShopIds.includes(shop.id)
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={assignedShopIds.includes(shop.id)}
                          onChange={() => toggleShop(shop.id)}
                          className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">{shop.name}</span>
                      </label>
                    ))}
                  </div>

                  {!formData.companyId && visibleShops.length === shops.length && (
                    <p className="text-xs text-gray-500 mt-3">
                      Astuce : sélectionnez d&apos;abord une entreprise pour filtrer la liste des boutiques.
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-blue-900 mb-2">Informations sur les rôles :</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li><strong>Super Admin :</strong> Accès complet, sans entreprise ni boutique</li>
              <li><strong>Admin :</strong> Accès général au système</li>
              <li><strong>Admin Entreprise :</strong> Rattaché à une entreprise</li>
              <li><strong>Admin Shop :</strong> Rattaché à une entreprise et à une ou plusieurs boutiques de cette entreprise</li>
              <li><strong>Manager :</strong> Rattaché à exactement une boutique ; se connecte avec l’email de cette boutique. Peut créer et modifier les opérations du shop, sans suppression (annulation de rendez-vous et désactivation restent possibles)</li>
            </ul>
          </div>

          <div className="flex justify-between pt-6 border-t">
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Réinitialiser
            </button>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => navigate('/admins')}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {loading ? 'Création...' : "Créer l'Admin"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminCreatePage;
